/**
 * Shared single-row status-bar renderer for history charts (asset + tracker).
 * Draws one rect per [start, end] interval, anchored at a y value — a category
 * row index on a status grid, or a data-coordinate lane Y on the main grid;
 * renderItem's api.coord resolves against whichever axis the series is bound to.
 */

export interface BarRowItem {
  value: [number, number]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [extra: string]: any
}

export function buildBarRowSeries(
  opts: {
    name: string
    anchorY: number
    data: BarRowItem[]
    colorFor: (dataIndex: number) => string
    xAxisIndex: number
    yAxisIndex: number
    barHeight?: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tooltip?: (dataItem: any) => string
    // in-bar label shown when the bar is wide enough
    label?: (dataIndex: number) => { text: string; color: string } | null
    z?: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    markPoint?: any
  } // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  const barHeight = opts.barHeight ?? 6
  return {
    type: 'custom',
    name: opts.name,
    xAxisIndex: opts.xAxisIndex,
    yAxisIndex: opts.yAxisIndex,
    clip: true,
    silent: false,
    data: opts.data,
    renderItem: (
      params: { dataIndex: number },
      api: { coord: (v: [number, number]) => [number, number]; value: (idx: number) => number }
    ) => {
      const startX = api.coord([api.value(0), opts.anchorY])[0]
      const endX = api.coord([api.value(1), opts.anchorY])[0]
      const centerY = api.coord([0, opts.anchorY])[1]
      const w = Math.max(endX - startX, 1)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const children: any[] = [
        {
          type: 'rect',
          shape: { x: startX, y: centerY - barHeight / 2, width: w, height: barHeight },
          style: { fill: opts.colorFor(params.dataIndex) },
        },
      ]
      const lbl = opts.label?.(params.dataIndex)
      if (lbl && w > 40) {
        children.push({
          type: 'text',
          x: startX + 3,
          y: centerY,
          style: { text: lbl.text, fontSize: 8, fill: lbl.color, textVerticalAlign: 'middle' },
        })
      }
      return { type: 'group', children }
    },
    encode: { x: [0, 1] },
    z: opts.z ?? 5,
    ...(opts.markPoint ? { markPoint: opts.markPoint } : {}),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...(opts.tooltip ? { tooltip: { formatter: (p: any) => opts.tooltip!(p.data) } } : {}),
  }
}
